import React, { useEffect } from 'react'
import { useConfiguration } from '../contexts/ConfigurationContext'
import ProviderSelector from '../components/ProviderSelector'
import InfrastructureConfig from '../components/InfrastructureConfig'
import CostDisplay from '../components/CostDisplay'
import LocationsDisplay from '../components/LocationsDisplay'
import ConfigurationActions from '../components/ConfigurationActions'
import RealTimePricing from '../components/RealTimePricing'
import ErrorMessage from '../components/ErrorMessage'
import LoadingSpinner from '../components/LoadingSpinner'
import { Zap, Shield, Globe, BarChart3 } from 'lucide-react'

function Home() {
  const { state, calculateCost, loadFilteredProviders } = useConfiguration()

  // Recalculate cost when configuration changes
  useEffect(() => {
    if (state.selectedProvider) {
      calculateCost()
    }
  }, [state.selectedProvider, state.vcpus, state.ramGb, state.storageGb])

  // Load initial providers
  useEffect(() => {
    loadFilteredProviders()
  }, [])

  if (state.loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-6">
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-secondary-900 mb-4">
            Enterprise Cloud Infrastructure Planning
          </h1>
          <p className="text-lg text-secondary-600 font-medium mb-8 max-w-3xl mx-auto leading-relaxed">
            Configure, compare, and optimize your cloud infrastructure with real-time pricing 
            and transparent cost analysis across all major cloud providers.
          </p>
        </div>
        
        {/* Feature highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div className="flex flex-col items-center space-y-2">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary-600" />
            </div>
            <span className="text-xs font-semibold text-secondary-700">Real-time Pricing</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <div className="w-10 h-10 bg-gradient-to-br from-success-100 to-success-200 rounded-lg flex items-center justify-center">
              <Shield className="h-5 w-5 text-success-600" />
            </div>
            <span className="text-xs font-semibold text-secondary-700">GDPR Compliant</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <div className="w-10 h-10 bg-gradient-to-br from-accent-100 to-accent-200 rounded-lg flex items-center justify-center">
              <Globe className="h-5 w-5 text-accent-600" />
            </div>
            <span className="text-xs font-semibold text-secondary-700">Global Coverage</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <div className="w-10 h-10 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-lg flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-secondary-600" />
            </div>
            <span className="text-xs font-semibold text-secondary-700">Cost Analytics</span>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {state.error && <ErrorMessage message={state.error} />}

      {/* Configuration Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Provider Selection */}
        <div className="space-y-6">
          <ProviderSelector />
          {state.selectedProvider && (
            <RealTimePricing 
              providerId={state.selectedProvider.id}
              region={state.selectedProvider.locations?.[0]?.region}
            />
          )}
        </div>

        {/* Middle Column - Infrastructure Configuration */}
        <div className="space-y-6">
          <InfrastructureConfig />
          {state.selectedProvider && <LocationsDisplay />}
        </div>

        {/* Right Column - Cost & Actions */}
        <div className="space-y-6">
          <CostDisplay />
          <ConfigurationActions />
        </div>
      </div>


    </div>
  )
}

export default Home 